import { Alert, Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { FunctionComponent, useContext, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import LibraryRuleService from '../../../shared/services/library-rule/library-rule.service';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';

// Deleting a library item needs a choice the standard confirmation dialog can't express
// (it resolves to a plain boolean), so this is its own small dialog.
export const PartnerLibraryDeleteDialog: FunctionComponent<{
  open: boolean,
  libraryRuleId: string,
  libraryRuleName: string,
  pushedClientCount: number,
  closeHandler: () => void,
  onDeleted: () => void,
  // 'rule' | 'analytic report'.
  resourceLabel?: string,
  // Why a copy might have to be kept rather than removed: a rule already used on committed
  // emissions data, a report wired into a scheduled send.
  inUseExplanation?: string,
  // Defaults to the rules service so existing callers are unchanged.
  deleteResource?: (id: string, removeFromClients: boolean) => Promise<{ message?: string }>,
}> = ({
  open, libraryRuleId, libraryRuleName, pushedClientCount, closeHandler, onDeleted,
  resourceLabel = 'rule',
  inUseExplanation = 'A client who has already used this rule on committed emissions data keeps their copy, because it can\'t be deleted without affecting their figures.',
  deleteResource,
}) => {
  const libraryRuleService = new LibraryRuleService()
  const runDelete = deleteResource ?? ((id: string, removeFromClients: boolean) => libraryRuleService.delete(id, removeFromClients))
  const userNotificationsContext = useContext(UserNotificationsContext)

  // Default to leaving the copies alone: deleting your own library entry shouldn't silently
  // reach into someone else's account and remove data they may be relying on.
  const [removeFromClients, setRemoveFromClients] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const submitHandler = () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    runDelete(libraryRuleId, removeFromClients)
      .then(response => {
        setIsSubmitting(false)
        userNotificationsContext.openSnackbar({ open: true, message: response.message || 'Deleted successfully' })
        onDeleted()
        closeHandler()
      })
      .catch(err => {
        console.log(err)
        setIsSubmitting(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
  }

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title={`Delete "${libraryRuleName}"?`}
      maxWidth='sm'
      cancelHandler={closeHandler}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      submitButtonText='Delete'
      submitButtonLoadingText='Deleting'
    >
      {pushedClientCount === 0 && <Typography variant='body2'>
        This {resourceLabel} hasn't been pushed to any clients. Deleting it can't be undone.
      </Typography>}

      {pushedClientCount > 0 && <>
        <Typography variant='body2' sx={{ mb: 2 }}>
          This {resourceLabel} has been pushed to {pushedClientCount} client{pushedClientCount === 1 ? '' : 's'}.
          What should happen to their copies?
        </Typography>

        <RadioGroup
          value={removeFromClients ? 'remove' : 'leave'}
          onChange={(e) => setRemoveFromClients(e.target.value === 'remove')}
        >
          <FormControlLabel
            value='leave'
            control={<Radio size='small' />}
            label={<Box>
              <Typography variant='body2'>Leave their copies in place</Typography>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                The {resourceLabel} stays in each client account and becomes theirs to edit or delete.
              </Typography>
            </Box>}
            sx={{ alignItems: 'flex-start', mb: 1 }}
          />
          <FormControlLabel
            value='remove'
            control={<Radio size='small' />}
            label={<Box>
              <Typography variant='body2'>Remove it from their accounts too</Typography>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                The {resourceLabel} is deleted from every client it was pushed to.
              </Typography>
            </Box>}
            sx={{ alignItems: 'flex-start' }}
          />
        </RadioGroup>

        {removeFromClients && <Alert severity='info' sx={{ mt: 2 }}>
          {inUseExplanation} Those are released to the client instead, and you'll see exactly
          which in the confirmation.
        </Alert>}

        <Stack sx={{ mt: 2 }}>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            Deleting the library {resourceLabel} can't be undone.
          </Typography>
        </Stack>
      </>}
    </DialogWrapper>
  )
}
